import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Clock } from "lucide-react";
import { useEffect } from "react";
import { useServerGamesStatus, pickStatus, statusLabel } from "@/lib/serverGameStatus";

export const Route = createFileRoute("/_authenticated/matches")({
  head: () => ({ meta: [{ title: "Games — Brothers Club" }] }),
  component: MatchesPage,
});

function MatchesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: games } = useQuery({
    queryKey: ["games_active"],
    queryFn: async () => {
      const { data } = await supabase
        .from("games")
        .select("*")
        .eq("active", true)
        .order("position");
      return data ?? [];
    },
  });

  const { data: bulk } = useServerGamesStatus();

  useEffect(() => {
    const channel = supabase
      .channel("games-realtime-matches")
      .on("postgres_changes", { event: "*", schema: "public", table: "games" }, () => {
        qc.invalidateQueries({ queryKey: ["games_active"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  return (
    <div>
      <AppHeader />
      <div className="gradient-header px-4 pb-6 pt-2 -mt-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-primary-foreground">Today's Games</h2>
            <p className="text-xs text-white/70">Pick a game and start playing.</p>
          </div>
          <button
            onClick={() => navigate({ to: "/bets" })}
            className="rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-bold text-primary-foreground"
          >
            My Bets
          </button>
        </div>
      </div>
      <div className="space-y-3 px-4 py-4">
        {(games ?? []).map((g) => {
          const st = pickStatus(bulk, g.id);
          const isOpen = !!st?.is_betting_open;
          return (
          <div key={g.id} className="overflow-hidden rounded-2xl bg-card shadow-card">
            <div className="flex items-center gap-3 p-4">
              <span
                className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-xl font-black text-white shadow-purple"
                style={{ background: g.color ?? "var(--color-primary)" }}
              >
                {g.name.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-base font-bold">{g.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                    isOpen ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"
                  }`}>
                    {isOpen ? "Open" : "Closed"}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Open: {g.open_time?.slice(0, 5)}</span>
                  <span>Close: {g.close_time?.slice(0, 5)}</span>
                  <span>Result: {g.result_time?.slice(0, 5)}</span>
                </div>
                <p className="mt-1 text-[11px]">Min ₹{g.min_bet} · Max ₹{g.max_bet} · Bet until {g.close_time?.slice(0, 5)}</p>
              </div>
              <button
                disabled={!g.active || !isOpen}
                onClick={() => navigate({ to: "/play/$gameId", params: { gameId: g.id } })}
                className="flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-purple disabled:opacity-50"
              >
                <Play className="h-3.5 w-3.5" /> Play
              </button>
            </div>
            {!isOpen && (
              <div className="bg-destructive/10 py-1.5 text-center text-[10px] font-bold uppercase text-destructive">
                {g.active ? (statusLabel(st?.status) || "Betting Closed.") : "Currently closed"}
              </div>
            )}
          </div>
          );
        })}
        {(!games || games.length === 0) && (
          <div className="rounded-2xl bg-card p-10 text-center text-sm text-muted-foreground shadow-card">
            No games available.
          </div>
        )}
      </div>
    </div>
  );
}
